import { useExperiment } from "@Light/app/experiments";
import { useFeature } from "@Light/app/features";
import { chainQueries } from "@Light/components/app/QueryLoader";
import { useAccount } from "@Light/components/page/account";
import { useOverlay } from "@Light/components/page/Overlay";
import { useLight } from "@Light/services/light";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Route, Routes, useNavigate } from "react-router-dom";
import { EnrollRoute } from "../routes";
import { precedingCompletedScreens } from "./backStack";
import { EnrollmentContext, type UseEnrollment } from "./context";
import { selectActiveScreen } from "./screen";
import type {
  EnrollmentScreen,
  Requirements,
  RequirementsPatch,
} from "./types";

export type EnrollmentFlowProps = {
  /** Where the flow is mounted, e.g. "enroll"; screens route as `/${basePath}/${path}`. */
  basePath: string;
  screens: EnrollmentScreen[];
  onFinalize?: () => void | Promise<void>;
};

const joinPath = (base: string, path: string): string => `/${base}${path}`;

async function runMutation<T>(
  operation: () => Promise<T>,
  opts?: { throwOnError?: boolean },
): Promise<T | undefined> {
  try {
    return await operation();
  } catch (err) {
    if (opts?.throwOnError) {
      throw err;
    }
    return undefined;
  }
}

export function EnrollmentFlow({
  basePath,
  screens,
  onFinalize,
}: EnrollmentFlowProps) {
  const routerNavigate = useNavigate();
  const overlay = useOverlay();
  const isSingleColumnExperiment =
    useExperiment("single-column-rate-screen") === "variant";
  const isRequiredPlanRate = useFeature("CUSTOMER_CLIENT_REQUIRED_PLAN_RATE");
  const singleColumn = isSingleColumnExperiment || isRequiredPlanRate;
  const { enrollment } = useAccount();
  const { useUpdateAccountMutation, useFinalizeEnrollmentMutation } =
    useLight();
  const [finalizeEnrollment, finalizeMutation] =
    useFinalizeEnrollmentMutation();
  const [initialRequirements] = useState<Requirements>(
    () => enrollment.requirements,
  );
  const [requirements, setRequirements] = useState<Requirements>(
    () => enrollment.requirements,
  );

  // Use separate mutations so that `nextMutation` is only modified on calls
  // `next`. Saves are done silently to preserve next's mutation state.
  const [updateAccountNext, nextMutation] = useUpdateAccountMutation();
  const [updateAccountSave, saveMutation] = useUpdateAccountMutation();
  // Dedicated to `saveAndFinalize` so its combined state never reflects an
  // earlier screen's save.
  const [updateAccountForFinalize, finalizeSaveMutation] =
    useUpdateAccountMutation();

  // Browser-style history: Back moves the cursor instead of dropping forward
  // steps, so re-advancing replays the original path.
  const [nav, setNav] = useState<UseEnrollment["nav"]>({
    stack: [],
    cursor: 0,
  });
  const seeded = useRef(false);

  const transitionTo = (path: string, swap: () => void): Promise<void> =>
    overlay.transitionExpanded(
      !singleColumn &&
        (screens.find((screen) => screen.path === path)?.expanded ?? false),
      swap,
    );

  const navigate: UseEnrollment["navigate"] = (path, opts = {}) => {
    setNav((n) => {
      // Replace current screen with custom return screen and push new screen
      if (opts.returnTo) {
        const stack = [...n.stack.slice(0, n.cursor), opts.returnTo, path];
        return { stack, cursor: stack.length - 1 };
      }

      // Replace current screen by popping it from stack
      if (opts.replace && !opts.keepInStack) {
        const stack = [...n.stack.slice(0, n.cursor), path];
        return { stack, cursor: stack.length - 1 };
      }

      // No-op case where we try to navigate to current screen
      if (n.stack[n.cursor] === path) return n;

      // Navigating to an already-visited screen moves the cursor back to its
      // latest occurrence so advancing replays the screens after it, unless
      // `keepInStack` asks to push a fresh entry instead (e.g. a re-quoted rate
      // should move forward, not step back into the address screen ahead of it
      // in the stack). Latest, so a re-quote's fresh rate wins over an earlier
      // one and its later screens are the ones replayed.
      if (!opts.keepInStack) {
        const visited = n.stack.lastIndexOf(path);
        if (visited >= 0) return { ...n, cursor: visited };
      }

      // Otherwise push to the top of the stack
      const stack = [...n.stack.slice(0, n.cursor + 1), path];
      return { stack, cursor: stack.length - 1 };
    });

    // Keep browser history in step with the visited stack: returnTo swaps the
    // current entry for the return screen and pushes the target on top, so
    // native Back lands on the return screen rather than the URL we came from.
    if (opts.returnTo) {
      routerNavigate(joinPath(basePath, opts.returnTo), { replace: true });
      routerNavigate(joinPath(basePath, path));
      return;
    }

    routerNavigate(joinPath(basePath, path), {
      replace: opts.replace === true,
    });
  };

  useLayoutEffect(() => {
    if (seeded.current) return;
    seeded.current = true;

    const entry = selectActiveScreen(
      screens,
      initialRequirements,
      initialRequirements,
      new Set(),
    );

    const stack = [
      ...precedingCompletedScreens(screens, entry.path, initialRequirements),
      entry.path,
    ];
    setNav({ stack, cursor: stack.length - 1 });

    routerNavigate(joinPath(basePath, entry.path), { replace: true });

    if (entry.expanded && !singleColumn) {
      void overlay.transitionExpanded(true);
    }
  }, [
    screens,
    initialRequirements,
    basePath,
    routerNavigate,
    overlay,
    singleColumn,
  ]);

  // Handle browser back / forward
  useEffect(() => {
    const onPopState = () => {
      const { pathname } = window.location;
      if (nav.stack.length === 0) return;
      if (pathname === joinPath(basePath, nav.stack[nav.cursor])) return;

      const screen = screens.find(
        (candidate) => joinPath(basePath, candidate.path) === pathname,
      );
      if (!screen) return;

      setNav((n) => {
        // Earliest occurrence: with a `keepInStack` duplicate (e.g. a re-quoted
        // rate appearing twice), backing onto it must land on the first copy so
        // the screens before it — the zip steps — stay reachable.
        const cursor = n.stack.indexOf(screen.path);
        if (cursor >= 0) return { ...n, cursor };
        const stack = [...n.stack.slice(0, n.cursor + 1), screen.path];
        return { stack, cursor: stack.length - 1 };
      });

      void overlay.transitionExpanded(
        !singleColumn && (screen.expanded ?? false),
        undefined,
        { fadeOut: false },
      );
    };

    window.addEventListener("popstate", onPopState);
    return () => {
      window.removeEventListener("popstate", onPopState);
    };
  }, [nav, screens, basePath, overlay, singleColumn]);

  const patchRequirements = async (
    update: typeof updateAccountSave,
    payload: RequirementsPatch,
  ): Promise<Requirements> => {
    const updated = (
      await update({ enrollment_requirements: payload }).unwrap()
    ).enrollment.requirements;
    setRequirements(updated);
    return updated;
  };

  const save = (payload: RequirementsPatch) =>
    runMutation(() => patchRequirements(updateAccountSave, payload));

  const next = async (
    payload?: RequirementsPatch,
    opts?: {
      replace?: boolean;
      throwOnError?: boolean;
      returnTo?: EnrollRoute;
    },
  ) => {
    nextMutation.reset();
    let updated: Requirements;
    if (payload && Object.keys(payload).length > 0) {
      const result = await runMutation(
        () => patchRequirements(updateAccountNext, payload),
        opts,
      );
      if (result === undefined) {
        return;
      }
      updated = result;
    } else {
      updated = requirements;
    }

    if (nav.cursor < nav.stack.length - 1) {
      const cursor = nav.cursor + 1;
      const path = nav.stack[cursor];
      await transitionTo(path, () => {
        setNav((n) => ({ ...n, cursor }));
        routerNavigate(joinPath(basePath, path), { replace: opts?.replace });
      });
      return;
    }

    const path = selectActiveScreen(
      screens,
      updated,
      initialRequirements,
      new Set(nav.stack),
    ).path;
    await transitionTo(path, () => {
      navigate(path, opts);
    });
  };

  const back =
    nav.cursor > 0
      ? () => {
          const cursor = nav.cursor - 1;
          const path = nav.stack[cursor];
          void transitionTo(path, () => {
            setNav((n) => ({ ...n, cursor }));
            routerNavigate(joinPath(basePath, path), { replace: true });
          });
        }
      : undefined;

  const saveAndFinalize = (payload: RequirementsPatch) =>
    runMutation(async () => {
      finalizeSaveMutation.reset();
      finalizeMutation.reset();
      await patchRequirements(updateAccountForFinalize, payload);
      await finalizeEnrollment().unwrap();
      await onFinalize?.();
    });

  const value: UseEnrollment = {
    screens,
    nav,
    requirements,
    initialRequirements,
    finalized: enrollment.is_enrollment_finalized ?? false,
    nextMutation,
    saveMutation,
    saveAndFinalizeMutation: {
      ...chainQueries([finalizeSaveMutation, finalizeMutation]),
      error: finalizeSaveMutation.error ?? finalizeMutation.error,
      reset: () => {
        finalizeSaveMutation.reset();
        finalizeMutation.reset();
      },
    },
    save,
    next,
    back,
    saveAndFinalize,
    navigate: (path, opts) => {
      void transitionTo(path, () => {
        navigate(path, opts);
      });
    },
    href: (path: string) => joinPath(basePath, path),
  };

  return (
    <EnrollmentContext.Provider value={value}>
      <Routes>
        {screens.map((screen) => {
          const Screen = screen.component;
          return (
            <Route key={screen.path} path={screen.path} element={<Screen />} />
          );
        })}
      </Routes>
    </EnrollmentContext.Provider>
  );
}
